import { HttpError } from './errors';
import {
  DEFAULT_TIMEOUT_MS,
  buildErrorFromResponse,
  buildUrl,
  createAbortController,
  normalizeRequestBody,
  normalizeUnknownError,
  safeParseJson,
  safeParseText,
} from './utils';

import type { HttpErrorResponse, HttpSuccessResponse, RequestConfig } from './types';

const mergeHeaders = (
  baseHeaders: HeadersInit | undefined,
  extraHeaders: HeadersInit | undefined,
): Headers => {
  const headers = new Headers(baseHeaders);

  if (extraHeaders) {
    const additionalHeaders = new Headers(extraHeaders);
    additionalHeaders.forEach((value, key) => {
      headers.set(key, value);
    });
  }

  return headers;
};

export const httpClient = async <TResponse>(
  input: string,
  config: RequestConfig = {},
): Promise<HttpSuccessResponse<TResponse>> => {
  const {
    method = 'GET',
    timeoutMs = DEFAULT_TIMEOUT_MS,
    queryParams,
    parseAs = 'json',
    body,
    headers,
    ...restConfig
  } = config;

  const url = buildUrl(input, queryParams);
  const { controller, timeoutId } = createAbortController(timeoutMs);
  const normalizedBody = normalizeRequestBody(body);

  try {
    const requestHeaders = mergeHeaders(undefined, headers);

    if (normalizedBody.contentType && !requestHeaders.has('Content-Type')) {
      requestHeaders.set('Content-Type', normalizedBody.contentType);
    }

    const requestInit: RequestInit = {
      ...restConfig,
      method,
      headers: requestHeaders,
      signal: controller.signal,
    };

    if (normalizedBody.body !== undefined) {
      requestInit.body = normalizedBody.body;
    }

    const response = await fetch(url, requestInit);

    if (!response.ok) {
      const errorPayload =
        (await safeParseJson<HttpErrorResponse>(response)) ??
        ({
          message:
            (await safeParseText(response)) || `Request failed with status ${response.status}`,
          status: response.status,
        } satisfies HttpErrorResponse);

      throw buildErrorFromResponse(
        response.status,
        errorPayload,
        `Request failed with status ${response.status}`,
      );
    }

    if (parseAs === 'response') {
      return {
        data: response as TResponse,
        status: response.status,
        headers: response.headers,
      };
    }

    if (parseAs === 'text') {
      return {
        data: (await response.text()) as TResponse,
        status: response.status,
        headers: response.headers,
      };
    }

    if (parseAs === 'blob') {
      return {
        data: (await response.blob()) as TResponse,
        status: response.status,
        headers: response.headers,
      };
    }

    const data = await safeParseJson<TResponse>(response);

    return {
      data: (data ?? (null as TResponse)) as TResponse,
      status: response.status,
      headers: response.headers,
    };
  } catch (error) {
    throw normalizeUnknownError(error);
  } finally {
    clearTimeout(timeoutId);
  }
};

export const get = async <TResponse>(
  input: string,
  config?: Omit<RequestConfig, 'method' | 'body'>,
): Promise<HttpSuccessResponse<TResponse>> => {
  return httpClient<TResponse>(input, {
    ...config,
    method: 'GET',
  });
};

export const post = async <TResponse>(
  input: string,
  body?: RequestConfig['body'],
  config?: Omit<RequestConfig, 'method' | 'body'>,
): Promise<HttpSuccessResponse<TResponse>> => {
  return httpClient<TResponse>(input, {
    ...config,
    method: 'POST',
    ...(body !== undefined ? { body } : {}),
  });
};

export const put = async <TResponse>(
  input: string,
  body?: RequestConfig['body'],
  config?: Omit<RequestConfig, 'method' | 'body'>,
): Promise<HttpSuccessResponse<TResponse>> => {
  return httpClient<TResponse>(input, {
    ...config,
    method: 'PUT',
    ...(body !== undefined ? { body } : {}),
  });
};

export const patch = async <TResponse>(
  input: string,
  body?: RequestConfig['body'],
  config?: Omit<RequestConfig, 'method' | 'body'>,
): Promise<HttpSuccessResponse<TResponse>> => {
  return httpClient<TResponse>(input, {
    ...config,
    method: 'PATCH',
    ...(body !== undefined ? { body } : {}),
  });
};

export const del = async <TResponse>(
  input: string,
  config?: Omit<RequestConfig, 'method' | 'body'>,
): Promise<HttpSuccessResponse<TResponse>> => {
  return httpClient<TResponse>(input, {
    ...config,
    method: 'DELETE',
  });
};

export { HttpError };
